import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import AttributeInfoBar from '../attributeInfoBar';
import ReportChart from './reportChart';
import ReportComments from './reportComments';
import ReportScore from './reportScore';
import ReportResultsGrid from './reportResultGrid';
import { getEnpsResults } from '../../../utility/reportUtils';
import {
  isArrayEmpty,
  prepareSubmitedSurveyParticipantsCount,
} from '../../../utility/helpers';
import { SURVEY_TYPES } from '../../../constants/survey';

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
  },
}));

const NpsReviewReport = ({
  translations,
  review,
  surveyType,
  isCompleted = true,
  hasCompletedCount = false,
  shouldDisableIndividualScores = false,
  reviewersCount = 0,
  isSummaryView = false,
  onViewIndividualScores = () => {},
}) => {
  const classes = useStyles();
  const finishedCount = prepareSubmitedSurveyParticipantsCount(
    review.participants
  );

  const { chartData, replies, metrics } =
    useMemo(
      () =>
        review?.answers &&
        getEnpsResults(
          review.answers,
          isSummaryView ? review.replies : review.comments,
          finishedCount
        ),
      [review, isSummaryView, finishedCount]
    ) || {};

  const reviewName = surveyType === SURVEY_TYPES.ENPS ? 'eNPS' : 'NPS';
  const hasAnswers = review.answers;
  const hasChartData = hasAnswers && !isArrayEmpty(chartData);
  const hasReplies = hasAnswers && !isArrayEmpty(replies);
  const hasScore = metrics?.score !== undefined;
  const noResultsMessage = isCompleted
    ? translations.noChartResultsSingleDone
    : translations.noChartResultsSingleInProgress;

  return (
    <div className={classes.root}>
      <AttributeInfoBar
        translations={translations}
        attribute={{ ...review, name: reviewName }}
        averageScore={metrics?.score}
        reviewersCount={reviewersCount}
        hasCompletedCount={hasCompletedCount}
        shouldDisableIndividualScores={shouldDisableIndividualScores}
        onViewIndividualScores={onViewIndividualScores}
        isScorePercentage
        isExpanded
        shouldShowScores
      />
      {hasScore && (
        <ReportScore translations={translations} score={metrics?.score} />
      )}
      {hasAnswers && (
        <ReportResultsGrid translations={translations} results={metrics} />
      )}
      {hasAnswers && (
        <ReportChart
          translations={translations}
          chartData={chartData}
          hasData={hasChartData}
          noDataMessage={noResultsMessage}
        />
      )}
      {hasReplies && (
        <ReportComments label={translations.commentsLabel} comments={replies} />
      )}
    </div>
  );
};

NpsReviewReport.propTypes = {
  translations: PropTypes.object.isRequired,
  surveyType: PropTypes.string.isRequired,
  review: PropTypes.shape({}).isRequired,
  reviewersCount: PropTypes.number,
  isCompleted: PropTypes.bool,
  shouldDisableIndividualScores: PropTypes.bool,
  onViewIndividualScores: PropTypes.func,
  hasCompletedCount: PropTypes.bool,
  isSummaryView: PropTypes.bool,
};

export default NpsReviewReport;
